import {
  validateHeatmapDateRange,
  toExclusiveToDate,
  MAX_HEATMAP_WINDOW_DAYS
} from '../../../services/heatmap.service';

export { validateHeatmapDateRange, toExclusiveToDate, MAX_HEATMAP_WINDOW_DAYS };

/**
 * Returns today's ISO date string in UTC (YYYY-MM-DD).
 * @returns {string}
 */
export function getTodayIsoDate() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Returns an ISO date string N days prior in UTC (YYYY-MM-DD).
 * @param {number} days 
 * @returns {string}
 */
export function getDaysAgoIsoDate(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split('T')[0];
}

/**
 * Preset date range options for the Heatmap Filter Bar.
 * All presets respect the 31-day backend safety limit.
 */
export const HEATMAP_DATE_PRESETS = [
  { id: '2d', label: '2D', days: 2 },
  { id: '7d', label: '7D', days: 7 },
  { id: 'custom', label: 'Custom', days: null }
];

/**
 * Returns the start and end dates for a given preset ID.
 * @param {string} presetId 
 * @returns {{ startDate: string, endDate: string }}
 */
export function getPresetDateRange(presetId) {
  const today = getTodayIsoDate();
  const preset = HEATMAP_DATE_PRESETS.find(p => p.id === presetId);
  if (!preset || preset.days === null) {
    return { startDate: getDaysAgoIsoDate(7), endDate: today };
  }
  return {
    startDate: getDaysAgoIsoDate(preset.days),
    endDate: today
  };
}

/**
 * Formats YYYY-MM-DD string to readable DD-MM-YYYY format.
 * @param {string} dateStr 
 * @returns {string}
 */
export function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
}

/**
 * Formats a start and end date range into a consistent display string.
 * @param {string} start 
 * @param {string} end 
 * @returns {string}
 */
export function formatDisplayDateRange(start, end) {
  if (!start || !end) return 'Select Measurement Window';
  return `${formatDisplayDate(start)} — ${formatDisplayDate(end)}`;
}

/**
 * Maps percentage coordinates to canvas pixel coordinates.
 * @param {number} xPercent - 0 to 100
 * @param {number} yPercent - 0 to 100
 * @param {number} width - Canvas width in pixels
 * @param {number} height - Canvas height in pixels
 * @returns {{ x: number, y: number }}
 */
export function densityToCanvas(xPercent, yPercent, width, height) {
  const safeX = Math.max(0, Math.min(100, Number(xPercent) || 0));
  const safeY = Math.max(0, Math.min(100, Number(yPercent) || 0));
  return {
    x: (safeX / 100) * width,
    y: (safeY / 100) * height
  };
}

/**
 * Normalizes error messages for user-friendly display in heatmap views.
 * @param {Error|string} error 
 * @param {string} fallback 
 * @returns {string}
 */
export function normalizeHeatmapError(error, fallback = 'An unexpected error occurred') {
  if (!error) return fallback;
  const status = error?.status;
  const rawMsg = typeof error === 'string' ? error : (error.message || '');
  const lower = rawMsg.toLowerCase();

  // 409 Conflict - Candidate configuration changed or layout updated
  if (
    status === 409 ||
    lower.includes('409') ||
    lower.includes('candidate configuration changed') ||
    lower.includes('mine and confirm again')
  ) {
    return 'Re-mine journeys — layout or settings have been updated';
  }

  // 429 Quota Exceeded - Limit reached
  if (
    status === 429 ||
    lower.includes('429') ||
    lower.includes('flows per site collection limit reached') ||
    lower.includes('quota exceeded') ||
    lower.includes('too many requests')
  ) {
    return 'Flows per site collection limit reached';
  }

  // 404 Not Found - Candidate is no longer available
  if (
    status === 404 ||
    lower.includes('candidate is no longer available')
  ) {
    return 'The selected candidate is no longer available. Re-mine journeys to refresh candidates.';
  }

  // 422 Unprocessable Entity - Heatmap query budget exceeded
  if (status === 422 || lower.includes('budget exceeded') || (lower.includes('422') && !lower.includes('network'))) {
    return 'Date range too broad. Narrow date filter to fewer days';
  }

  // 403 Forbidden - Heatmap capture is disabled
  if (
    status === 403 ||
    lower.includes('capture is disabled') ||
    lower.includes('disabled for this domain')
  ) {
    return 'Heatmap is disabled for this domain in Site Collection settings';
  }

  if (rawMsg) return rawMsg;
  return fallback;
}

/**
 * Checks whether an error is a backend 422 or query budget exceeded error.
 * @param {Error|string} error 
 * @returns {boolean}
 */
export function isBudgetExceededError(error) {
  if (!error) return false;
  const status = error?.status;
  const msg = typeof error === 'string' ? error : (error.message || '');
  const lower = msg.toLowerCase();
  return status === 422 || lower.includes('budget exceeded') || lower.includes('422');
}

/**
 * Formats a byte number to human-readable string (B, KB, MB, GB, TB).
 * @param {number|string} bytes
 * @param {number} [decimals=1]
 * @returns {string}
 */
export function formatBytes(bytes, decimals = 1) {
  if (bytes == null || bytes === '') return '-';
  const b = Number(bytes);
  if (isNaN(b) || b < 0) return '-';
  if (b === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  const idx = Math.min(i, sizes.length - 1);
  return `${parseFloat((b / Math.pow(k, idx)).toFixed(dm))} ${sizes[idx]}`;
}

/**
 * Formats an ISO date/time string to YYYY-MM-DD HH:mm:ss in UTC.
 * @param {string|Date} dateVal
 * @returns {string}
 */
export function formatDateTime(dateVal) {
  if (!dateVal) return '-';
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '-';
    return d.toISOString().replace('T', ' ').slice(0, 19);
  } catch {
    return '-';
  }
}
